import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";

import { Observable } from 'rxjs';

import { WindowService, WindowRef, WindowCloseResult, WindowSettings } from '@progress/kendo-angular-dialog';

import { LogService } from "../log.service";
import { UserService } from "../user.service";
import { ModalComponent } from "./modal/modal.component";
import { HeaderBarMessageComponent } from "./header-bar-messages/header-bar-message/header-bar-message.component";
import { HeaderBarMessageDeleteComponent } from "./header-bar-messages/header-bar-message-delete/header-bar-message-delete.component";
import { HeaderBarMessageProgressComponent } from "./header-bar-messages/header-bar-message-progress/header-bar-message-progress.component";
import { MessageType, MessageTypeIcons, HeaderBarMessageHeaderInfo } from './message.model';
import { DeleteMessageDetails } from './header-bar-messages/header-bar-message-delete/delete-message-detail.model';
import { DeleteMessageBarInfo } from './header-bar-messages/header-bar-message-delete/delete-message.model';
import { ProgressMessageDetails } from './header-bar-messages/header-bar-message-progress/progress-message-detail.model';
import { ProgressMessageBarInfo } from './header-bar-messages/header-bar-message-progress/progress-message.model';

@Injectable({
  providedIn: 'root',
})

export class MessageService {

  private nextStackedTopPosition: number = 1;

  constructor(private kendoSevice: WindowService,
    private logService: LogService,
    private userService: UserService,
    private messageUI: MatDialog) {
  }

  /**
   * Show a modal dialog
   * 
   * @param modalTitle : Title of the dialog
   * @param modalMessage : Message to be displayed - keep it short
   * @param messageType : See MessageType
   * @param txtRight : Text to display on right button - leave blank to hide the button
   * @param txtLeft : Text to display on left button - leave blank to hide the button
   * @param txtPrimary : The text of the button that will be the primary button.  Determines the class of the button.
   * 
   * @returns : the text of the button that was pressed
   */
  public showModalMessage(modalTitle: string,
    modalMessage: string,
    messageType: MessageType,
    txtRight: string,
    txtLeft: string,
    txtPrimary: string): Observable<string> {

    const dialogConfig = new MatDialogConfig();
    dialogConfig.maxWidth = "none";  // remove any default max-width on the overlay pane so we can set our own width on the dialog
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = "ot-modal-message-panel";
    dialogConfig.backdropClass = "ot-modal-panel-backdrop";
    dialogConfig.data = {
      title: modalTitle,
      message: modalMessage,
      type: messageType,
      icon: MessageTypeIcons[messageType],
      txtRight: txtRight,
      txtLeft: txtLeft,
      txtPrimary: txtPrimary
    };

    var message = "showModalMessage : " + modalTitle + " : " + modalMessage + " : " + messageType;
    this.logService.logMessage(message);

    return this.messageUI.open(ModalComponent, dialogConfig).afterClosed();
  }

  /**
   * General header bar message 
   * @param hbm_info Header bar information structure
   * @param messageDetails Any per item detail rows for message
   */
  public showHeaderBarMessage(hbm_info: HeaderBarMessageHeaderInfo) {

    let settings: WindowSettings = {
      content: HeaderBarMessageComponent,
      resizable: false,
      draggable: false,
      top: this.getNextStackedPosition()
    };

    const windowRef: WindowRef = this.kendoSevice.open(settings);
    this.incrementStackedPosition();

    const nmc = windowRef.content.instance;
    nmc.hbi = hbm_info;

    windowRef.result.subscribe((result: any) => {
      if (result instanceof WindowCloseResult) {
        this.decrementStackedPosition(windowRef.window.instance.top);
        windowRef.close();
        this.logService.logMessage("showHeaderBarMessage : " + hbm_info.message + " : " + hbm_info.messageType);
      }
    });
    this.userService.isLoggedOnChanged$.subscribe(() => windowRef.close());
  }

  /**
   * Delete focused header bar message
   * @param hbm_info Header bar information structure
   * @param messageDetails Any per item detail rows for message 
   */
  public showDeleteHeaderBarMessage(dhbi: DeleteMessageBarInfo, deleteDetails: DeleteMessageDetails) {

    let settings: WindowSettings = {
      content: HeaderBarMessageDeleteComponent,
      resizable: false,
      draggable: false,
      top: this.getNextStackedPosition()
    };

    const windowRef: WindowRef = this.kendoSevice.open(settings);
    this.incrementStackedPosition();

    const nmc = windowRef.content.instance;
    nmc.hbi = dhbi;
    nmc.deletes = deleteDetails;

    if (deleteDetails.details.length > 0)
      nmc.showCaretDown = true;

    windowRef.result.subscribe((result: any) => {
      if (result instanceof WindowCloseResult) {
        this.decrementStackedPosition(windowRef.window.instance.top);
        windowRef.close();
        this.logService.logMessage("showHeaderBarMessage : " + dhbi.message + " : " + dhbi.messageType);
      }
    });
    this.userService.isLoggedOnChanged$.subscribe(() => windowRef.close());
  }

  public showProgressHeaderBarMessage(phmb_info: ProgressMessageBarInfo, details: ProgressMessageDetails) {

    let settings: WindowSettings = {
      content: HeaderBarMessageProgressComponent,
      resizable: false,
      draggable: false,
      top: this.getNextStackedPosition()
    };

    const windowRef: WindowRef = this.kendoSevice.open(settings);
    this.incrementStackedPosition();

    const nmc = windowRef.content.instance;
    nmc.hbi = phmb_info;
    nmc.details = details;

    if (details.details.length > 0)
      nmc.showCaretDown = true;

    windowRef.result.subscribe((result: any) => {
      if (result instanceof WindowCloseResult) {
        this.decrementStackedPosition(windowRef.window.instance.top);
        windowRef.close();
        this.logService.logMessage("showHeaderBarMessage : " + phmb_info.message + " : " + phmb_info.messageType);
      }
    });
    this.userService.isLoggedOnChanged$.subscribe(() => windowRef.close());
  }

  private getNextStackedPosition(): number {
    return this.nextStackedTopPosition;
  }

  private incrementStackedPosition() {
    this.nextStackedTopPosition = this.nextStackedTopPosition + 4;  // in pixels
  }

  private decrementStackedPosition(top: number) {
    if (top == this.nextStackedTopPosition - 4) {
      this.nextStackedTopPosition = this.nextStackedTopPosition - 4;  // in pixels
    }
  }
}
